import { useEffect, useState } from 'react'
import { Deal, User } from '../../types'
import { hasPermission } from '../../lib/permissions'
import { supabase } from '../../lib/supabase'
import { apiHeadersOrThrow } from '../../lib/authHeader'
import './PpsrLodgementPanel.css'

/**
 * PPSR lodgement controls for the deal workspace.
 *
 * Lodging and discharging a security interest are LEGAL ACTS against the
 * national register, not internal state changes. They are therefore:
 *   - manual only, never fired by a workflow trigger
 *   - gated on the 'perm-ppsr-lodge' permission
 *   - behind an explicit confirmation step that names the vehicle and states
 *     what will happen on the register
 *
 * The endpoints record to ppsr_registrations and workflow_logs, so there is
 * an audit trail regardless of who clicked.
 */

interface Props {
  deal: Deal
  user: User
  onChanged: () => void
}

type Pending = 'register' | 'discharge' | null

export default function PpsrLodgementPanel({ deal, user, onChanged }: Props) {
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [certUrl, setCertUrl] = useState<string | null>(null)
  const [certError, setCertError] = useState<string | null>(null)
  const [fetching, setFetching] = useState(false)

  const d = deal as any
  const vin: string | null = d.autograb_vin || d.vehicle_vin || null
  const lodgement: string = d.ppsr_lodgement_status || 'none'
  const isLodged = lodgement === 'lodged'
  const certPath: string | null = d.ppsr_certificate_path || null
  const certRef: string | null = d.ppsr_reference_number || null

  /*
   * The register builds the certificate after it answers the search, and the background job
   * that collects it gives up after eighteen seconds. When it loses that race the deal keeps
   * its search result and its certificate number but no document, and nothing used to go back
   * for it. This does, on the number already stored - it never re-runs the search, so pressing
   * it cannot spend another search fee.
   */
  const fetchCertificate = async () => {
    setFetching(true)
    setNotice(null)
    try {
      const res = await fetch('/api/ppsr/fetch-certificate', {
        method: 'POST',
        headers: await apiHeadersOrThrow(),
        body: JSON.stringify({ dealId: deal.id }),
      })
      const json = await res.json()
      setNotice(json.message || (json.success ? 'Certificate filed.' : 'Could not fetch the certificate.'))
      if (json.success) onChanged()
    } catch (err) {
      setNotice('Could not reach the register: ' + ((err as Error)?.message || 'unknown error'))
    } finally {
      setFetching(false)
    }
  }

  /*
   * The signed link to the filed PPSR search certificate.
   *
   * The bucket is private, so the link has to be signed. It is signed here, when the panel
   * loads, rather than when the button is pressed - a window.open that happens after an
   * await has lost the user's click, and every browser treats it as a pop-up and blocks it.
   * Fetching the link first makes the control an ordinary anchor, which nothing blocks.
   *
   * An hour is long enough for anyone to read a deal and click, and short enough that a link
   * pasted into a chat is dead before anyone else follows it.
   */
  useEffect(() => {
    let live = true
    if (!certPath) {
      setCertUrl(null)
      setCertError(null)
      return
    }
    supabase.storage
      .from('ppsr-certificates')
      .createSignedUrl(certPath, 3600)
      .then(({ data, error }) => {
        if (!live) return
        if (error || !data?.signedUrl) {
          setCertUrl(null)
          setCertError((error as Error)?.message || 'the register did not return a link')
          return
        }
        setCertUrl(data.signedUrl)
        setCertError(null)
      })
      .catch((err: unknown) => {
        if (!live) return
        setCertUrl(null)
        setCertError((err as Error)?.message || 'unknown error')
      })
    return () => {
      live = false
    }
  }, [certPath])


  if (!hasPermission(user, 'perm-ppsr-lodge')) return null

  const run = async (action: 'register' | 'discharge') => {
    setPending(null)
    setBusy(true)
    setNotice(null)
    try {
      const res = await fetch('/api/ppsr/' + action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dealId: deal.id }),
      })
      const json = await res.json()
      if (!json.success) throw new Error(json.message || 'PPSR request failed')
      setNotice(
        action === 'register'
          ? 'PPSR registration submitted. The registration number appears once PPSR confirms it, usually within a few seconds.'
          : 'PPSR registration discharged.'
      )
      onChanged()
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'PPSR request failed')
    } finally {
      setBusy(false)
    }
  }

  const vehicle =
    [d.year, d.make, d.model].filter(Boolean).join(' ').trim() || 'this vehicle'

  const confirmBody =
    pending === 'register'
      ? 'This registers a security interest on the national PPSR register against ' +
        vehicle +
        ' (VIN ' +
        (vin || 'not set') +
        '). It is a legal act, it is publicly searchable, and it stays in force until discharged.'
      : 'This removes the security interest against ' +
        vehicle +
        ' (VIN ' +
        (vin || 'not set') +
        ') from the national PPSR register. Once discharged it cannot be reinstated - a new registration would be required.'

  return (
    <div className="ppsr-panel">
      <div className="ppsr-panel-head">
        <span className="ws-label">PPSR lodgement</span>
        <div className="ppsr-head-right">
          <span className={'ppsr-chip ppsr-chip-' + (isLodged ? 'lodged' : 'none')}>
            {lodgement}
          </span>
          {certUrl ? (
            <a
              className="ppsr-btn ppsr-btn-ghost"
              href={certUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open the filed PPSR search certificate"
            >
              View PPSR Certificate
            </a>
          ) : certRef && !certPath ? (
            <button
              className="ppsr-btn ppsr-btn-ghost"
              disabled={fetching}
              onClick={fetchCertificate}
              title={
                'The search is done and paid for but its certificate never arrived. This fetches it on ' +
                'reference ' +
                certRef +
                ' - it does not run another search.'
              }
            >
              {fetching ? 'Fetching...' : 'Fetch PPSR Certificate'}
            </button>
          ) : (
            <span
              className="ppsr-btn ppsr-btn-ghost ppsr-btn-off"
              title={
                certError
                  ? 'The certificate is on file but the link could not be signed: ' + certError
                  : certPath
                    ? 'Fetching the certificate link'
                    : 'No PPSR search has been run against this deal yet'
              }
            >
              View PPSR Certificate
            </span>
          )}
        </div>
      </div>

      <div className="ppsr-panel-facts">
        <span>
          Search status: <strong>{d.ppsr_status || 'not checked'}</strong>
        </span>
        <span>
          Reference: <strong>{d.ppsr_reference_number || '-'}</strong>
        </span>
        <span>
          Certificate: <strong>{d.ppsr_certificate_path ? 'stored' : 'not stored'}</strong>
        </span>
      </div>

      {notice ? <div className="ppsr-panel-notice">{notice}</div> : null}

      <div className="ppsr-panel-actions">
        {!isLodged ? (
          <button
            className="ppsr-btn ppsr-btn-primary"
            disabled={busy || !vin}
            onClick={() => setPending('register')}
          >
            {busy ? 'Working...' : 'Lodge PPSR registration'}
          </button>
        ) : (
          <button
            className="ppsr-btn ppsr-btn-danger"
            disabled={busy}
            onClick={() => setPending('discharge')}
          >
            {busy ? 'Working...' : 'Discharge registration'}
          </button>
        )}
        {!vin ? (
          <span className="ppsr-panel-hint">No VIN on this deal - lodgement needs one.</span>
        ) : null}
      </div>

      {pending ? (
        <div className="ppsr-modal-overlay" onClick={() => setPending(null)}>
          <div className="ppsr-modal" onClick={(e) => e.stopPropagation()}>
            <h4>
              {pending === 'register'
                ? 'Lodge a PPSR security interest?'
                : 'Discharge this PPSR registration?'}
            </h4>
            <p>{confirmBody}</p>
            <p className="ppsr-modal-check">Are you sure you would like to proceed?</p>
            <div className="ppsr-modal-actions">
              <button className="ppsr-btn" onClick={() => setPending(null)}>
                Cancel
              </button>
              <button
                className={
                  'ppsr-btn ' +
                  (pending === 'register' ? 'ppsr-btn-primary' : 'ppsr-btn-danger')
                }
                onClick={() => run(pending)}
              >
                {pending === 'register' ? 'Yes, lodge it' : 'Yes, discharge it'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
